<template>
  <div class="result-filters-container">
    <div class="shrink-row filters-header">
      <p>{{ $t('app.campaign.result.filters.compatibility') }}</p>
      <Button v-if="!isCampaignPending && !isCampaignV1(campaign)" class="square outline-white manual-book-button" v-tooltip="$t('app.campaign.result.panel.manualButton')" @click="downloadEvalManualPdf">
        <template #prefix>
          <BookIcon style="width: 18px"></BookIcon>
        </template>
      </Button>
    </div>
    <CircleSlider v-model="appliedFilters.compatibilityScore"></CircleSlider>
    <div class="filters-body">
      <div class="filter-container">
        <p class="filter-container-title">{{ $t('app.campaign.result.filters.dates.title') }}</p>
        <div class="date-filters">
          <p class="date-label">{{ $t('app.campaign.result.filters.dates.invitationLabel') }}</p>
          <div class="date-range-container">
            <div class="date-picker">
              <p>{{ $t('app.campaign.result.filters.dates.from') }}</p>
              <VueDatePicker
                class="invite-date-from"
                :dark="isDark"
                :locale="locale"
                :hide-input-icon="true"
                :enable-time-picker="false"
                :clearable="true"
                :teleport="true"
                :cancelText="locale == 'fr' ? 'Annuler' : 'Cancel'"
                :selectText="locale == 'fr' ? 'Sélectionner' : 'Select'"
                @cleared="invitationRange = DateRange.NONE"
                v-model="appliedFilters.invitationDateFrom"
              />
            </div>
            <div class="date-picker">
              <p>{{ $t('app.campaign.result.filters.dates.to') }}</p>
              <VueDatePicker
                class="invite-date-to"
                :dark="isDark"
                :locale="locale"
                :teleport="true"
                :enable-time-picker="false"
                :hide-input-icon="true"
                :clearable="true"
                :cancelText="locale == 'fr' ? 'Annuler' : 'Cancel'"
                :selectText="locale == 'fr' ? 'Sélectionner' : 'Select'"
                @cleared="invitationRange = DateRange.NONE"
                v-model="appliedFilters.invitationDateTo"
              />
            </div>
          </div>
          <div class="date-buttons-group">
            <div :class="{selected: invitationRange === DateRange.MONTH}" class="date-button" @click="setInviteDateToThisMonth(); invitationRange = DateRange.MONTH">{{ $t('app.campaign.result.filters.dates.buttons.thisMonth') }}</div>
            <div :class="{selected: invitationRange === DateRange.WEEK}" class="date-button" @click="setInviteDateToThisWeek(); invitationRange = DateRange.WEEK">{{ $t('app.campaign.result.filters.dates.buttons.thisWeek') }}</div>
            <div :class="{selected: invitationRange === DateRange.TODAY}" class="date-button" @click="setInviteDateToToday(); invitationRange = DateRange.TODAY">{{ $t('app.campaign.result.filters.dates.buttons.today') }}</div>
          </div>
          <p class="date-label">{{ $t('app.campaign.result.filters.dates.evalEndLabel') }}</p>
          <div class="date-range-container">
            <div class="date-picker">
              <p>{{ $t('app.campaign.result.filters.dates.from') }}</p>
              <VueDatePicker
                class="invite-date-from"
                :dark="isDark"
                :locale="locale"
                :hide-input-icon="true"
                :enable-time-picker="false"
                :teleport="true"
                :clearable="true"
                :cancelText="locale == 'fr' ? 'Annuler' : 'Cancel'"
                :selectText="locale == 'fr' ? 'Sélectionner' : 'Select'"
                @cleared="evaluationRange = DateRange.NONE"
                v-model="appliedFilters.evaluationEndDateFrom"
              />
            </div>
            <div class="date-picker">
              <p>{{ $t('app.campaign.result.filters.dates.to') }}</p>
              <VueDatePicker
                class="invite-date-to"
                :dark="isDark"
                :locale="locale"
                :teleport="true"
                :enable-time-picker="false"
                :hide-input-icon="true"
                :clearable="true"
                :cancelText="locale == 'fr' ? 'Annuler' : 'Cancel'"
                :selectText="locale == 'fr' ? 'Sélectionner' : 'Select'"
                @cleared="evaluationRange = DateRange.NONE"
                v-model="appliedFilters.evaluationEndDateTo"
              />
            </div>
          </div>
          <div class="date-buttons-group">
            <div :class="{selected: evaluationRange === DateRange.MONTH}" class="date-button" @click="setEvaluationDateToThisMonth(); evaluationRange = DateRange.MONTH">{{ $t('app.campaign.result.filters.dates.buttons.thisMonth') }}</div>
            <div :class="{selected: evaluationRange === DateRange.WEEK}" class="date-button" @click="setEvaluationDateToThisWeek(); evaluationRange = DateRange.WEEK">{{ $t('app.campaign.result.filters.dates.buttons.thisWeek') }}</div>
            <div :class="{selected: evaluationRange === DateRange.TODAY}" class="date-button" @click="setEvaluationDateToToday(); evaluationRange = DateRange.TODAY">{{ $t('app.campaign.result.filters.dates.buttons.today') }}</div>
          </div>
        </div>
      </div>
      <div class="filter-container">
        <p class="filter-container-title">{{ $t('app.campaign.result.panel.skills.title') }}</p>
        <div class="skill-filters">
          <div class="item" v-for="skill in allSkills">
            <div class="item-header">
              <p>{{ $t(`skills.${skill.id}.name`) }}</p>
              <div class="separator"></div>
            </div>
            <BarSlider v-model="appliedFilters.skills[(skill.id) as string]"/>
          </div>
        </div>
        
      </div>
      <div v-if="!isCampaignPending && !isCampaignV1(campaign)" class="filter-container">
        <p class="filter-container-title">{{ $t('app.campaign.result.panel.styles.title') }}</p>
        <Styles :selectedStyles="appliedFilters.selectedStyles" @updateSelected="selectedStylesUpdate"></Styles>
      </div>
      <div class="filter-container">
        <div class="badges-header">
          <span class="filter-container-title">{{ $t('app.campaign.result.panel.success.title') }}</span>
          <InfoIcon style="width: 14px; margin-bottom: -1px;" v-tooltip="$t('app.campaign.result.filters.achievements.tooltip')"></InfoIcon>
        </div>
        <BadgesSelector :selectedBadges="appliedFilters.selectedBadges" @updateSelected="selectedBadgesUpdate"></BadgesSelector>
      </div>
    </div>
    <div class="buttons-inline">
      <Button class="icon icon-left outline-white" icon="bi-arrow-return-left" :label="$t('app.campaign.result.filters.buttons.cancel')" @click="emits('closeFilters')"></Button>
      <Button class="icon icon-left outline-white" icon="bi-arrow-repeat" :label="$t('app.campaign.result.filters.buttons.reset')" @click="resetFilters"></Button>
      <Button class="icon icon-left green" icon="bi-check2" :label="$t('app.campaign.result.filters.buttons.applyFilters') + ` (${filterCount})`" @click="save"></Button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

  .buttons-inline {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }

  .result-filters-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;

    padding: 24px;
    border: 1px solid var(--light-grey-color);
    border-radius: 8px;
    background-color: var(--white-color);

    .shrink-row {
      flex-grow: 0;
      flex-shrink: 0;
      flex-basis: auto;
      min-height: 0;
    }

    .filters-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      .manual-book-button {
        border-radius: 4px;
        border: 1px solid var(--light-grey-color);
      }

      p {
        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
      }
    }

    .filters-body {
      flex: 1;
      width: 100%;

      display: flex;
      flex-direction: row;
      gap: 16px;

      .filter-container {
        flex-grow: 1;
        flex-shrink: 1;
        flex-basis: 25%;
        border: 1px solid var(--light-grey-color);
        padding: 24px;

        display: flex;
        flex-direction: column;
        gap: 16px;

        .badges-header {
          display: flex;
          gap: 8px;
          align-items: center;

          i::before {
            font-weight: 600 !important;
          }
        }

        .filter-container-title {
          color: var(--secondary-color);
          font-family: $bioSans;
          font-size: 16px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
        }
      }
    }

    .date-filters {
      .date-label {
        padding-bottom: 8px;
      }
    }

    .date-range-container {
      display: flex;
      flex-direction: row;
      gap: 8px;

      :deep(.dp__input) {
        padding: 8px 12px;
        border: 1px solid var(--light-grey-color);

        &:hover {
          border: 1px solid var(--secondary-color);
        }
      }

      .date-picker {
        flex-basis: 50%;

        p {
          overflow: hidden;
          color: var(--secondary-color);
          text-overflow: ellipsis;
          font-family: $bioSans;
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;

          padding-bottom: 8px;
        }
      }
    }

    .date-buttons-group {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      padding-top: 8px;
      padding-bottom: 24px;

      .date-button {
        cursor: pointer;

        border: 1px solid $secondary-color;
        border-radius: 2px;
        text-align: center;
        flex-shrink: 1;
        flex-basis: calc(50% - 3px);
        padding: 8px;

        background-color: var(--light-grey-color);

        &:hover {
          border: 1px solid var(--secondary-color);
        }

        &.selected {
          color: $secondary-color;
          background-color: $primary-color;
        }
      }
    }

    .skill-filters {
      flex-grow: 1;
      flex-basis: 0;
      display: flex;
      flex-direction: column;
      gap: 24px;
      width: 100%;
      padding-right: 14px;
      overflow-y: auto;
      overflow-x: hidden;

      &::-webkit-scrollbar {
        padding-left: 4px;
        width: 3px;
        border-radius: 24px;
        background-color: var(--light-grey-color);
      }

      &::-webkit-scrollbar-thumb {
        background: $primary-color;
        padding: 4px;
        width: 3px;
        border-radius: 24px;
      }
      
      .item {
        .item-header {
          padding-bottom: 19px;
          display: flex;
          align-items: center;
          gap: 8px;

          p {
            color: var(--secondary-color);
            font-family: $bioSans;
            font-size: 14px;
            font-style: normal;
            font-weight: 600;
            line-height: normal;
          }

          .separator {
            flex: 1;
            border-top: 1px solid var(--secondary-color);
            height: 1px;
          }
        }
      }
    }
  }
</style>

<script setup lang="ts">
import '@vuepic/vue-datepicker/dist/main.css';
import VueDatePicker from '@vuepic/vue-datepicker';
import cloneDeep from 'lodash/cloneDeep';
import { useDark } from '@vueuse/core';
import i18n from '@/i18n';

// Components and views
import CircleSlider from '@/components/campaigns/filters/CircleSlider.vue';
import BarSlider from '@/components/campaigns/filters/BarSlider.vue';
import Styles from '@/components/campaigns/filters/StylesSelector.vue';
import BadgesSelector from '@/components/campaigns/filters/BadgesSelector.vue';

// Assets
import evalManualPdfUrl from '@/assets/pdf/Manuel des évaluations de Yuzu.pdf';
import evalManualPdfEnUrl from '@/assets/pdf/Yuzu assessment manual.pdf';

import InfoIcon from '@/assets/images/icon/campaign/result_panel/info.svg?component';
import BookIcon from '@/assets/images/icon/campaign/result_panel/book.svg?component';

// Queries
import { useCampaign } from '@/queries/campaigns.query';

const route = useRoute();
const isDark = useDark();
const { t, locale } = useI18n();
const { skills, isCampaignV1 } = useSkillsStore();

const { data: campaign, isPending: isCampaignPending } = useCampaign();

export interface SelectedStyles {
  [name: string]: string[],
}

enum DateRange {
  MONTH,
  WEEK,
  TODAY,
  NONE,
}

export interface SelectedBadges {
  [name: string]: boolean,
}

export interface Filters {
  invitationDateFrom?: Date,
  invitationDateTo?: Date,
  evaluationEndDateFrom?: Date,
  evaluationEndDateTo?: Date,

  compatibilityScore: [number, number],

  skills?: {
    [id: string]: [number, number],
  },
  selectedStyles?: SelectedStyles,
  selectedBadges?: SelectedBadges,
}

interface Props {
  appliedFilters?: Filters,
}

const campaignVersion = computed(() => {
  return campaign.value?.campaign_version || "3.0.0";
});

const isSkillCompatible = (skillID: string) => {
  const skill = skills.find((skill) => {
    if (skill?.id === skillID || skill?.id_v1 === skillID) return true;
    return false;
  });
  if (skill && skill.incompatible_versions && skill.incompatible_versions.includes(campaignVersion.value)) {
    return false;
  }
  return true;
};

const allSkills = computed(() => {
  const mods = skills.filter((module) => {
    if (module.type === 'style') return false;
    if (module.disabled) return false;

    return isSkillCompatible(module.id);
  })
  return mods.sort((mA, mB) => {
    const idA = t(`skills.${mA.id}.name`);
    const idB = t(`skills.${mB.id}.name`);
    return idA.localeCompare(idB);
  });
});

const emits = defineEmits<{
  (e: 'closeFilters'): void,
  (e: 'applyFilters', value: Filters): void,
}>();

const props = withDefaults(defineProps<Props>(), {
  appliedFilters: {
    invitationDateFrom: undefined,
    invitationDateTo: undefined,
    evaluationEndDateFrom: undefined,
    evaluationEndDateTo: undefined,

    compatibilityScore: [0, 99],

    skills: undefined,

    selectedStyles: undefined as SelectedStyles,
    selectedBadges: undefined as SelectedBadges,
  },
});

const evaluationRange = ref(DateRange.NONE);
const invitationRange = ref(DateRange.NONE);

const appliedFilters = ref(cloneDeep(props.appliedFilters) as Filters);

watch(() => props.appliedFilters, (v) => (appliedFilters.value = cloneDeep(v)));

if (!appliedFilters.value.skills) {
  resetSelectedSkills();
}

if (!appliedFilters.value.selectedStyles) {
  resetSelectedStyles();
}

if (!appliedFilters.value.selectedBadges) {
  resetSelectedBadges();
}

function resetSelectedSkills(): void {
  appliedFilters.value.skills = allSkills.value.reduce((acc, skill) => {
    acc[(skill.id) as string] = [0, 100];
    return acc;
  }, {});
}

function resetSelectedStyles(): void {
  appliedFilters.value.selectedStyles = skills.filter((module) => {
    if (module.type === 'skill') return false;
    if (module.disabled) return false;
    if (isCampaignV1(campaign.value)) return module.id;

    return true;
  }).reduce((acc, module) => {
    acc[(module.id) as string] = [];
    return acc;
  }, {});
}

function resetSelectedBadges(): void {
  const localeData = i18n.global.messages.value[locale.value];
  const badgeLocales = Object.keys(localeData.badges);
  
  appliedFilters.value.selectedBadges = {};
  badgeLocales.forEach((key) => {
    appliedFilters.value.selectedBadges[key] = false;
  });
}

function resetFilters() {
  appliedFilters.value.invitationDateFrom = undefined;
  appliedFilters.value.invitationDateTo = undefined;
  appliedFilters.value.evaluationEndDateFrom = undefined;
  appliedFilters.value.evaluationEndDateTo = undefined;

  appliedFilters.value.compatibilityScore = [0, 99];

  appliedFilters.value.skills = undefined;

  appliedFilters.value.selectedStyles = undefined as SelectedStyles;
  appliedFilters.value.selectedBadges = undefined as SelectedBadges;

  resetSelectedSkills();
  resetSelectedStyles();
  resetSelectedBadges();
}

function selectedStylesUpdate(updatedSelection) {
  appliedFilters.value.selectedStyles = updatedSelection;
}

function selectedBadgesUpdate(updatedSelection) {
  appliedFilters.value.selectedBadges = updatedSelection;
}

function downloadEvalManualPdf() {
  if (locale.value === 'fr') {
    window.open(evalManualPdfUrl, '_blank');
  } else {
    window.open(evalManualPdfEnUrl, '_blank');
  }
}

function save() {
  emits('applyFilters', appliedFilters.value);
  emits('closeFilters');
}



function setInviteDateToThisMonth() {
  const now = new Date();

  appliedFilters.value.invitationDateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
  appliedFilters.value.invitationDateTo = now;
}

function setInviteDateToThisWeek() {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);

  appliedFilters.value.invitationDateFrom = new Date(now.setDate(diff));
  appliedFilters.value.invitationDateTo = now;
}

function setInviteDateToToday() {
  const now = new Date();

  appliedFilters.value.invitationDateFrom = now;
  appliedFilters.value.invitationDateTo = now;
}


function setEvaluationDateToThisMonth() {
  const now = new Date();

  appliedFilters.value.evaluationEndDateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
  appliedFilters.value.evaluationEndDateTo = now;
}

function setEvaluationDateToThisWeek() {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);

  appliedFilters.value.evaluationEndDateFrom = new Date(now.setDate(diff));
  appliedFilters.value.evaluationEndDateTo = now;
}

function setEvaluationDateToToday() {
  const now = new Date();

  appliedFilters.value.evaluationEndDateFrom = now;
  appliedFilters.value.evaluationEndDateTo = now;
}

const filterCount = computed(() => {
  let count = 0;
  if (appliedFilters.value.compatibilityScore[0] !== 0 || appliedFilters.value.compatibilityScore[1] !== 99) {
    count += 1;
  }

  if (!!appliedFilters.value.evaluationEndDateFrom || !!appliedFilters.value.evaluationEndDateTo) count += 1;
  if (!!appliedFilters.value.invitationDateFrom || !!appliedFilters.value.invitationDateTo) count += 1;

  const haveSomeSkillFilter = Object.values(appliedFilters.value.skills).some((skill) => {
    return skill[0] !== 0 || skill[1] !== 100;
  });
  if (haveSomeSkillFilter) count += 1;

  const haveSomeStyleFilter = Object.values(appliedFilters.value.selectedStyles).some((style) => {
    return !!style.length;
  });
  if (haveSomeStyleFilter) count += 1;

  const haveSomeBadgeFilter = Object.values(appliedFilters.value.selectedBadges).some((style) => {
    return style;
  });
  if (haveSomeBadgeFilter) count += 1;

  return count;
});

</script>